// =================================================================
// RequireBrainHealthUser
//
// Guards routes that any active brain health identity should be able
// to access (community resident, entitled individual, or device user).
// Redirects to /signup if no active context exists.
//
// This is intentionally broader than RequireResident. Pages that
// should be community-resident-only still use RequireResident.
// Pages like /account that have per-type behavior use this guard
// and dispatch internally.
//
// Note: this guard does NOT gate on entitlement. A signed-up
// individual user without an active subscription should still be
// able to reach /account — the page itself shows the "Subscribe"
// CTA. Entitlement gating happens at the game-play layer.
// =================================================================

import { Navigate, useLocation } from 'react-router-dom';
import {
  useCurrentContext,
  CONTEXT_TYPES,
} from '../lib/useCurrentContext.jsx';

export default function RequireBrainHealthUser({ children }) {
  const { type, loading } = useCurrentContext();
  const location = useLocation();

  if (loading) {
    return (
      <div className="container" style={{ paddingTop: '4rem' }}>
        <p
          style={{
            fontFamily: 'var(--font-serif)',
            fontStyle: 'italic',
            color: 'var(--ink-faded)',
          }}
        >
          Loading…
        </p>
      </div>
    );
  }

  if (type === CONTEXT_TYPES.NONE) {
    // No active brain health identity. Send to /signup, which works
    // for both new-user signup and returning-user sign-in (magic-link
    // auth doesn't distinguish — same flow either way).
    //
    // We pass returnTo in router state so /signup can route them
    // back here after auth completes. Community residents who lost
    // their localStorage session would need to go to /login instead
    // — that's a separate path (login code, not email). For now,
    // /signup is the broadest fallback.
    return (
      <Navigate
        to="/signup"
        replace
        state={{ returnTo: location.pathname }}
      />
    );
  }

  return children;
}
